/* InputBaseControlComponent.js */

"use strict";

const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");

class InputBaseControlComponent extends EventEmitter {
    /**
     * @schema.name Input Base Control
     *
     * @schema.configuration.disabled.name disabled
     * @schema.configuration.disabled.type boolean
     * @schema.configuration.disabled.default false
     * @schema.configuration.disabled.required false
     *
     * @schema.configuration.invalid.name invalid
     * @schema.configuration.invalid.type boolean
     * @schema.configuration.invalid.default false
     * @schema.configuration.invalid.required false
     *
     * @schema.configuration.visible.name visible
     * @schema.configuration.visible.type boolean
     * @schema.configuration.visible.default true
     * @schema.configuration.visible.required false
     *
     * @schema.configuration.darkTheme.name darkTheme
     * @schema.configuration.darkTheme.type boolean
     * @schema.configuration.darkTheme.default false
     * @schema.configuration.darkTheme.required false
     *
     * @schema.configuration.theme.name theme
     * @schema.configuration.theme.type string
     * @schema.configuration.theme.default ""
     * @schema.configuration.theme.required false
     *
     * @schema.configuration.value.name value
     * @schema.configuration.value.type string
     * @schema.configuration.value.default undefined
     * @schema.configuration.value.required false
     *
     * @schema.configuration.title.name title
     * @schema.configuration.title.type string
     * @schema.configuration.title.default ""
     * @schema.configuration.title.required false
     *
     * @schema.configuration.required.name required
     * @schema.configuration.required.type boolean
     * @schema.configuration.required.default false
     * @schema.configuration.required.required false
     */

    constructor(configOptions) {
        super();
        this._invalid = _.get(configOptions, "invalid", false);
        this._value = _.get(configOptions, "value", undefined);
        this._disabled = _.get(configOptions, "disabled", false);
        this._visible = _.get(configOptions, "visible", true);
        this._darkTheme = _.get(configOptions, "darkTheme", false);
        this._theme = _.get(configOptions, "theme", this._darkTheme ? "dark" : "");
        this._required = _.get(configOptions, "required", false);
        this._title = _.get(configOptions, "title", "");
    }

    /*******************************************/
    /* START PRIVATE METHODS */
    /*******************************************/

    /**
     * Triggers the 'UserValue' event
     * @param {(string|object|boolean|number)} newVal - The new value of the control
     * @param {(string|object|boolean|number)} oldVal - The old value of the control
     * @param {event} [event] - The DOM event that triggered the value change (optional)
     * @private
     */
    _triggerUserValue(newVal, oldVal, event) {
        this.emit("UserValue", newVal, oldVal, event);
    }

    /**
     * Pass the DOM focus event up from the renderer as a 'UserFocus' Component event
     * @param {event} event - The DOM event that triggered the value change
     * @private
     */
    _triggerUserFocus(event) {
        this.emit("UserFocus", event);
    }

    /**
     * Pass the DOM blur event up from the renderer as a 'UserBlur' Component event
     * @param {event} event - The DOM event that triggered the value change
     * @private
     */
    _triggerUserBlur(event) {
        this.emit("UserBlur", event);
    }

    /**
     * Pass the DOM keydown event up from the renderer as a 'UserKeyDown' Component event
     * @param {event} event - The DOM event that triggered the value change
     * @private
     */
    _triggerUserKeyDown(event) {
        this.emit("UserKeyDown", event);
    }

    /**
     * Pass the Invalid event up from the renderer as a 'Invalid' Component event
     * @param {boolean} newVal - Current Validity of input control value
     * @param {boolean} oldVal - Previous Validity of input control value
     * @private
     */
    _triggerInvalidStatusEvent(newVal, oldVal) {
        this.emit("InvalidUserInput", newVal, oldVal);
    }

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /**
     * @schema.methods.setDisabled.name setDisabled
     * @schema.methods.setDisabled.description Sets the disabled state and returns an instance of the control for chainability
     * @schema.methods.setDisabled.returns Instance of the control
     * @schema.methods.setDisabled.parameters.newVal.name newVal
     * @schema.methods.setDisabled.parameters.newVal.description The new disabled state
     * @schema.methods.setDisabled.parameters.newVal.type boolean
     */
    setDisabled(newVal) {
        this._disabled = newVal;
        this.emit("Disabled", this._disabled);
        return this;
    }
    /**
     * @schema.methods.isDisabled.name isDisabled
     * @schema.methods.isDisabled.description Returns the disabled state
     * @schema.methods.isDisabled.returns boolean
     */
    isDisabled() {
        return this._disabled;
    }
    get disabled() {
        return this.isDisabled();
    }
    set disabled(newVal) {
        return this.setDisabled(newVal);
    }

    /**
     * @schema.methods.setValue.name setValue
     * @schema.methods.setValue.description Sets the value and returns an instance of the control for chainability
     * @schema.methods.setValue.returns Instance of the control
     * @schema.methods.setValue.parameters.newVal.name newVal
     * @schema.methods.setValue.parameters.newVal.description The new value
     * @schema.methods.setValue.parameters.newVal.type string|object|boolean|number
     * @schema.methods.setValue.parameters.wasTriggeredByUser.name wasTriggeredByUser
     * @schema.methods.setValue.parameters.wasTriggeredByUser.description Flag alerting that this was a user-triggered change (optional)
     * @schema.methods.setValue.parameters.wasTriggeredByUser.required false
     * @schema.methods.setValue.parameters.wasTriggeredByUser.type boolean
     */
    setValue(newVal, wasTriggeredByUser) {
        let oldVal = _.cloneDeep(this._value);
        this._value = newVal;
        this.emit("Value", this._value, oldVal, wasTriggeredByUser);
        return this;
    }
    /**
     * @schema.methods.getValue.name getValue
     * @schema.methods.getValue.description Returns the value of the control
     * @schema.methods.getValue.returns string|object|boolean|number
     */
    getValue() {
        return this._value;
    }
    get value() {
        return this.getValue();
    }
    set value(newVal) {
        return this.setValue(newVal);
    }

    /**
     * @schema.methods.setInvalid.name setInvalid
     * @schema.methods.setInvalid.description Sets the invalid state and returns an instance of the control for chainability
     * @schema.methods.setInvalid.returns Instance of the control
     * @schema.methods.setInvalid.parameters.newVal.name newVal
     * @schema.methods.setInvalid.parameters.newVal.description The new invalid state
     * @schema.methods.setInvalid.parameters.newVal.type boolean
     */
    setInvalid(newVal) {
        this._invalid = newVal;
        this.emit("Invalid", this._invalid);
        return this;
    }
    /**
     * @schema.methods.isInvalid.name isInvalid
     * @schema.methods.isInvalid.description Returns the invalid state of the control
     * @schema.methods.isInvalid.returns boolean
     */
    isInvalid() {
        return this._invalid;
    }
    get invalid() {
        return this.isInvalid();
    }
    set invalid(newVal) {
        return this.setInvalid(newVal);
    }

    /**
     * @schema.methods.setRequired.name setRequired
     * @schema.methods.setRequired.description Set if this control value can be left blank. If true, it can be left blank
     *                              else triggers validation when left blank
     * @schema.methods.setRequired.parameters.isRequired.name isRequired
     * @schema.methods.setRequired.parameters.isRequired.description If true, this control can be left blank
     *                              else triggers validation when left blank
     * @schema.methods.setRequired.parameters.isRequired.type boolean
     * @schema.methods.setRequired.returns Instance of the control
     */
    setRequired(isRequired) {
        if (this._required !== isRequired) {
            this._required = isRequired === true;
            this.emit("Required", this._required);
        }
        return this;
    }

    /**
     * @schema.methods.isRequired.name isRequired
     * @schema.methods.isRequired.description Returns true if this control value can be blank, false triggers validations if control value is blank
     * @schema.methods.isRequired.returns {boolean}
     */
    isRequired() {
        return this._required;
    }

    set required(isRequired) {
        return this.setRequired(isRequired);
    }

    get required() {
        return this.isRequired();
    }

    /**
     * @schema.methods.setDarkTheme.name setDarkTheme
     * @schema.methods.setDarkTheme.description Sets the dark theme state and returns an instance of the control for chainability
     * @schema.methods.setDarkTheme.returns Instance of the control
     * @schema.methods.setDarkTheme.parameters.newVal.name newVal
     * @schema.methods.setDarkTheme.parameters.newVal.description The new dark theme state
     * @schema.methods.setDarkTheme.parameters.newVal.type boolean
     */
    setDarkTheme(newVal) {
        this._darkTheme = newVal;
        this.emit("DarkTheme", this._darkTheme);
        return this;
    }
    /**
     * @schema.methods.isDarkTheme.name isDarkTheme
     * @schema.methods.isDarkTheme.description Returns the dark theme state of the control
     * @schema.methods.isDarkTheme.returns boolean
     */
    isDarkTheme() {
        return this._darkTheme;
    }
    get darkTheme() {
        return this.isDarkTheme();
    }
    set darkTheme(newVal) {
        this.setTheme(newVal ? "dark" : "light");
        return this.setDarkTheme(newVal);
    }

    /**
     * @schema.methods.setTheme.name setTheme
     * @schema.methods.setTheme.description Sets the theme name (light, dark, veil) and returns an instance of the control for chainability
     * @schema.methods.setTheme.returns Instance of the control
     * @schema.methods.setTheme.parameters.newVal.name newVal
     * @schema.methods.setTheme.parameters.newVal.description The new theme name
     * @schema.methods.setTheme.parameters.newVal.type string
     */
    setTheme(newVal) {
        let oldVal = this._theme;
        this._theme = newVal;
        this.emit("Theme", this._theme, oldVal);
        if (newVal === "dark" || newVal === "light") {
            this._darkTheme = newVal === "dark";
            this.emit("DarkTheme", this._darkTheme);
        }
        return this;
    }
    /**
     * @schema.methods.getTheme.name getTheme
     * @schema.methods.getTheme.description Returns the dark theme state of the control
     * @schema.methods.getTheme.returns string
     */
    getTheme() {
        return this._theme;
    }
    get theme() {
        return this.getTheme();
    }
    set theme(newVal) {
        return this.setTheme(newVal);
    }

    /**
     * @schema.methods.setVisible.name setVisible
     * @schema.methods.setVisible.description Sets the visible state and returns an instance of the control for chainability
     * @schema.methods.setVisible.returns Instance of the control
     * @schema.methods.setVisible.parameters.newVal.name newVal
     * @schema.methods.setVisible.parameters.newVal.description The new visible state
     * @schema.methods.setVisible.parameters.newVal.type boolean
     */
    setVisible(newVal) {
        this._visible = newVal;
        this.emit("Visible", this._visible);
        return this;
    }
    /**
     * @schema.methods.isVisible.name isVisible
     * @schema.methods.isVisible.description Returns the visible state of the control
     * @schema.methods.isVisible.returns boolean
     */
    isVisible() {
        return this._visible;
    }
    get visible() {
        return this.isVisible();
    }
    set visible(newVal) {
        this.setVisible(newVal);
    }

    /**
     * @schema.methods.setTitle.name setTitle
     * @schema.methods.setTitle.description Sets the title/tooltip and returns an instance of the control for chainability
     * @schema.methods.setTitle.returns Instance of the control
     * @schema.methods.setTitle.parameters.newVal.name newVal
     * @schema.methods.setTitle.parameters.newVal.description The title/tooltip that is displayed when hovered over the control
     * @schema.methods.setTitle.parameters.newVal.type string
     */
    setTitle(newVal) {
        this._title = newVal;
        this.emit("Title", this._title);
        return this;
    }

    /**
     * @schema.methods.getTitle.name getTitle
     * @schema.methods.getTitle.description Returns title/tooltip displayed when hovered over the control
     * @schema.methods.getTitle.returns string
     */
    getTitle() {
        return this._title;
    }

    get title() {
        return this._title;
    }

    set title(value) {
        this.setTitle(value);
    }

    /**
     * @schema.methods.show.name show
     * @schema.methods.show.description Makes the control visible and returns an instance of the control for chainability
     * @schema.methods.show.returns Instance of the control
     */
    show() {
        this.visible = true;
        return this;
    }

    /**
     * @schema.methods.hide.name hide
     * @schema.methods.hide.description Hides the control and returns an instance of the control for chainability
     * @schema.methods.hide.returns Instance of the control
     */
    hide() {
        this.visible = false;
        return this;
    }

    /**
     * @schema.methods.focus.name focus
     * @schema.methods.focus.description Sets focus inside of the control and returns an instance of the control for chainability
     * @schema.methods.focus.returns Instance of the control
     */
    focus() {
        this.emit("Focus");
        return this;
    }

    /**
     * @schema.methods.blur.name blur
     * @schema.methods.blur.description Removes focus (blurs the control) and returns an instance of the control for chainability
     * @schema.methods.blur.returns Instance of the control
     */
    blur() {
        this.emit("Blur");
        return this;
    }

    /**
     * @schema.methods.setAttribute.name setAttribute
     * @schema.methods.setAttribute.description Removes focus (blurs the control) and returns an instance of the control for chainability
     * @schema.methods.setAttribute.parameters.attrName.name attrName
     * @schema.methods.setAttribute.parameters.attrName.description The name of the HTML attribute to modify
     * @schema.methods.setAttribute.parameters.attrName.type string
     * @schema.methods.setAttribute.parameters.attrValue.name attrValue
     * @schema.methods.setAttribute.parameters.attrValue.description The value of the HTML attribute to modify
     * @schema.methods.setAttribute.parameters.attrValue.type string
     * @schema.methods.setAttribute.returns Instance of the control
     */
    setAttribute(attrName, attrValue) {
        if (!_.isUndefined(attrName)) {
            this.emit("SetAttribute", attrName, attrValue);
        } else {
            console.error("You must pass an attribute name to the setAttribute function.");
        }
        return this;
    }

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}

module.exports = InputBaseControlComponent;
